import { useQuery } from '@apollo/client';
import READ_LAST_RECURRING_PAYMENT from 'graphql/queries/readLastRecurringPayment';
import { useEffect, useState } from 'react';
import {
  Maybe,
  ReadSalesOrderRecurringPaymentStatusInput,
} from 'types/generatedSchemaTypes';

type RecurringPaymentResponse = {
  failureDate: string | null;
  isFailed: boolean | null;
  orderLineId: string | null;
  salesOrderUid: string | null;
  chargeUid: string | null;
  validity: {
    validFrom: string;
    validTo: string;
  } | null;
};

type ReadLastRecurringPaymentData = {
  readLastRecurringPayment: RecurringPaymentResponse | null;
};

type PollSalesOrderLastRecurringPaymentResult = {
  failureDate: string | null | undefined;
  isFailed: boolean | null | undefined;
  orderLineId: string | null | undefined;
  salesOrderUid: string | null | undefined;
  lastPollingCompleted: boolean;
  refetch: () => void;
};

type ReadLastRecurringPaymentVars = {
  input: ReadSalesOrderRecurringPaymentStatusInput;
};

const usePollSalesOrderLastRecurringPaymentStatus = (
  input: ReadSalesOrderRecurringPaymentStatusInput,
  skip: boolean = true
): PollSalesOrderLastRecurringPaymentResult => {
  const [lastPollingCompleted, setLastPollingCompleted] = useState(true);
  const [cachedFailureDate, setCachedFailureDate] = useState<string | null>(
    null
  );

  const { data, loading, startPolling, stopPolling, refetch } = useQuery<
    ReadLastRecurringPaymentData,
    ReadLastRecurringPaymentVars
  >(READ_LAST_RECURRING_PAYMENT, {
    variables: {
      input,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip,
  });

  const lastRecurringPayment = data?.readLastRecurringPayment;
  const currentFailureDate = lastRecurringPayment?.failureDate ?? null;

  // Update cached failure date when isFailed is false
  useEffect(() => {
    if (currentFailureDate) {
      setCachedFailureDate(currentFailureDate);
    }
  }, [currentFailureDate]);

  // No data yet looks the same as a successful payment, so wait for the
  // response before deciding. Otherwise a retry whose polling stops at once
  // never leaves the completed state and its consumers are never notified.
  const shouldStopPolling =
    !loading &&
    determineIfPollingShouldStop(
      currentFailureDate,
      cachedFailureDate,
      lastRecurringPayment?.isFailed ?? false
    );

  useEffect(() => {
    if (skip || !input) {
      return;
    }

    if (!shouldStopPolling) {
      startPolling(1000); // Poll every 1 second
      setLastPollingCompleted(false);
    } else {
      stopPolling();
      setLastPollingCompleted(true);
    }

    return () => {
      stopPolling();
      setLastPollingCompleted(true);
    };
  }, [input, skip, shouldStopPolling]);

  return {
    failureDate: lastRecurringPayment?.failureDate,
    isFailed: lastRecurringPayment?.isFailed,
    orderLineId: lastRecurringPayment?.orderLineId,
    salesOrderUid: lastRecurringPayment?.salesOrderUid,
    lastPollingCompleted,
    refetch,
  };
};

const determineIfPollingShouldStop = (
  currentFailureDate: Maybe<string>,
  cachedFailureDate: Maybe<string>,
  isFailed: boolean
): boolean => {
  if (currentFailureDate === null && isFailed === false) {
    return true;
  }

  // Stop polling if we have both dates and current failure date is after cached date
  if (currentFailureDate && cachedFailureDate) {
    return new Date(currentFailureDate) > new Date(cachedFailureDate);
  }

  // Continue polling if we have a current failure date but no cached date
  if (currentFailureDate && !cachedFailureDate) {
    return false;
  }

  return false;
};

export default usePollSalesOrderLastRecurringPaymentStatus;
